import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UndefinedInitialQueryOptions,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import axiosInstance from '@/plugins/axios';
import type { DefaultError } from '@tanstack/query-core';
import type {
  IAccountResponse,
  IAuth2FALoginResponse,
  IAuthLoginRequest,
  IAuthLoginResponse,
  IAuthLogoutResponse,
  IAuthRemoveUserRequest,
  IAuthSuccessfulLoginResponse,
  IAuthTotpRequest,
  IMeResponse
} from '@/types/services/auth.service';
import { isAxiosError } from 'axios';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import type { IAvatarGetResponse } from '@/types/services/avatar.service';

export const isAuthSuccessResponse = (
  responseData: IAuthLoginResponse
): responseData is IAuthSuccessfulLoginResponse => {
  return !Number.isNaN(Number((responseData as IAuthSuccessfulLoginResponse).uid));
};
export const isAuth2FAResponse = (
  responseData: IAuthLoginResponse
): responseData is IAuth2FALoginResponse => {
  return (
    (responseData as IAuth2FALoginResponse).userId !== undefined &&
    (responseData as IAuth2FALoginResponse).cacheId !== undefined &&
    (responseData as IAuth2FALoginResponse).twoFactor.length > 0
  );
};

/**
 * This service composable is used for all authentication related queries.
 */
const useAuthService = () => {
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();
  const queryClient = useQueryClient();

  const login = () =>
    useMutation<IAuthLoginResponse, DefaultError, IAuthLoginRequest>({
      mutationKey: ['login'],
      mutationFn: async ({ email, password }) =>
        (
          await axiosInstance.post('/auth', {
            email,
            password
          })
        ).data,
      throwOnError: (error) => {
        return !(isAxiosError(error) && error.response?.status === 401);
      }
    });

  const totpLogin = () =>
    useMutation<IAuthSuccessfulLoginResponse, DefaultError, IAuthTotpRequest>({
      mutationKey: ['validateTotpRequest'],
      mutationFn: async (requestData) => {
        return (await axiosInstance.post('/auth/2fa', requestData)).data;
      }
    });

  const meQueryOptions = (): UndefinedInitialQueryOptions<IMeResponse> =>
    queryOptions<IMeResponse>({
      queryKey: ['authenticatedQuery', userIndex, 'me'],
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get(`/u/${userIndex.value}/auth/me`, {
            signal
          })
        ).data;
      },
      enabled: () => queriesEnabled.value
    });

  const me = (): UseQueryReturnType<IMeResponse, DefaultError> => useQuery(meQueryOptions());

  const mePolling = (): UseQueryReturnType<IMeResponse, DefaultError> =>
    useQuery({
      ...meQueryOptions(),
      refetchInterval: import.meta.env.VITE_AUTH_QUERY_REFETCH_INTERVAL_SECONDS
        ? Number(import.meta.env.VITE_AUTH_QUERY_REFETCH_INTERVAL_SECONDS) * 1000
        : 600_000,
      refetchIntervalInBackground: true
    });

  function updateLoggedInUsersAvatars(data: IAccountResponse[]): void {
    data.forEach((account) => {
      queryClient.setQueryData<IAvatarGetResponse | null>(
        ['authenticatedQuery', account.uid.toString(), 'getAvatar'],
        (oldData) => {
          if (account.avatar) {
            return {
              ...oldData,
              avatar: account.avatar
              // Overriding the data with the new avatar
            } satisfies IAvatarGetResponse;
          }

          return oldData;
        }
      );
    });
  }

  const getAccountsQuery = (): UndefinedInitialQueryOptions<IAccountResponse[]> =>
    queryOptions<IAccountResponse[]>({
      queryKey: ['getAccounts'],
      queryFn: async ({ signal }) => {
        const res = await axiosInstance.get<IAccountResponse[]>(`/auth/accounts`, {
          signal
        });
        const data = res.data;
        if (!data || !Array.isArray(data)) {
          return [];
        }

        updateLoggedInUsersAvatars(data);
        return data;
      }
    });

  const getAccounts = () => useQuery(getAccountsQuery());

  const removeDeviceUser = () =>
    useMutation<void, DefaultError, IAuthRemoveUserRequest>({
      mutationKey: ['removeDeviceUser'],
      mutationFn: async ({ index }) =>
        (await axiosInstance.delete(`/auth/removeDeviceUser?index=${index}`)).data
    });

  // This will basically clear all the cookies
  const removeDeviceUsers = () =>
    useMutation<void, DefaultError>({
      mutationKey: ['removeDeviceUsers'],
      mutationFn: async () => (await axiosInstance.delete(`/auth/removeDeviceUsers`)).data
    });

  const logout = () =>
    useMutation<IAuthLogoutResponse, DefaultError, { userIndex: number } | void>({
      mutationKey: ['logout'],
      mutationFn: async (data) =>
        (await axiosInstance.post(`/u/${data?.userIndex ?? userIndex.value}/auth/logout`)).data
    });

  const logoutAllDeviceUsers = () =>
    useMutation<undefined, DefaultError>({
      mutationKey: ['logoutAll'],
      mutationFn: async () =>
        (await axiosInstance.post(`/u/${userIndex.value}/auth/logoutAll`)).data
    });

  const refresh = () =>
    useMutation<void, DefaultError, { userIndex: number }>({
      mutationKey: ['refresh'],
      mutationFn: async ({ userIndex }) => {
        await axiosInstance.post(`/u/${userIndex}/auth/refresh`);
      }
    });
  return {
    login,
    totpLogin,
    me,
    mePolling,
    meQueryOptions,
    getAccountsQuery,
    getAccounts,
    removeDeviceUser,
    removeDeviceUsers,
    logout,
    logoutAllDeviceUsers,
    refresh
  };
};

export default useAuthService;
